"use client";

import Link from "next/link";
import { ArrowRight, BookOpen } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";

import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import type { HelpCategorySummary } from "@/lib/help-api";

export function HelpCategoryGrid({
  categories,
}: {
  categories: HelpCategorySummary[];
}) {
  const locale = useLocale();
  const t = useTranslations("help");

  if (categories.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-neutral-200 p-8 text-center text-sm text-muted dark:border-neutral-800 dark:text-muted-dark">
        {t("emptyCategories")}
      </div>
    );
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {categories.map((cat) => {
        const articleCount = cat.articles?.length ?? 0;
        return (
          <Link
            key={cat.id}
            href={`/${locale}/help/category/${cat.slug}`}
            className="group focus:outline-none"
          >
            <Card className="h-full">
              <CardHeader>
                <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-neutral-100 text-neutral-700 transition-colors group-hover:bg-red-500 group-hover:text-white dark:bg-neutral-900 dark:text-neutral-300">
                  <BookOpen className="h-5 w-5" />
                </div>
                <CardTitle className="flex items-center justify-between gap-2">
                  <span>{cat.title}</span>
                  <ArrowRight className="h-4 w-4 text-neutral-400 transition-transform group-hover:translate-x-0.5 group-hover:text-red-500 dark:text-neutral-600" />
                </CardTitle>
                {cat.description && (
                  <CardDescription className="line-clamp-2">
                    {cat.description}
                  </CardDescription>
                )}
                <div className="mt-2 text-xs text-muted dark:text-muted-dark">
                  {t("articleCount", { count: articleCount })}
                </div>
              </CardHeader>
            </Card>
          </Link>
        );
      })}
    </div>
  );
}
